import { useForm } from "@refinedev/antd";
import type { HttpError } from "@refinedev/core";
import { useGo, useInvalidate } from "@refinedev/core";

import { Card, Form, Input, Switch, Select, Space, Button, InputNumber } from "antd";

const { TextArea } = Input;

interface AIStyle {
  id: string;
  style: string;
  system_prompt: string;
  llm_model: string;
  temperature: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AIStyleFormValues {
  style: string;
  system_prompt: string;
  llm_model: string;
  temperature: number;
  is_active: boolean;
}

export const AIStyleCreateForm = () => {
  const go = useGo();
  const invalidate = useInvalidate();

  const { formProps, formLoading } = useForm<
    AIStyle,
    HttpError,
    AIStyleFormValues
  >({
    resource: "settings/ai-styles",
    action: "create",
    redirect: false,
    mutationMode: "pessimistic",
    onMutationSuccess: () => {
      invalidate({ resource: "settings/ai-styles", invalidates: ["resourceAll"] });
      go({
        to: "/settings/ai-styles",
        type: "replace",
        options: { keepQuery: true },
      });
    },
    successNotification: () => ({
      type: "success",
      message: "Успешно",
      description: "AI стиль создан",
    }),
    errorNotification: (error) => ({
      type: "error",
      message: "Не удалось сохранить",
      description:
        error?.message || "Проверьте обязательные поля и повторите попытку",
    }),
  });

  const handleFinish = async (values: AIStyleFormValues) => {
    const payload = {
      ...values,
      style: values.style.trim(),
    };
    await formProps.onFinish?.(payload);
  };

  return (
    <Card title="Новый AI стиль" loading={formLoading} bordered>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          is_active: true,
          llm_model: "deepseek-chat",
          temperature: 0.7,
        }}
        autoComplete="off"
      >
        <Form.Item
          label="Название стиля"
          name="style"
          rules={[
            { required: true, message: "Введите название стиля" },
            { min: 3, message: "Минимум 3 символа" },
          ]}
          tooltip="Уникальное название для идентификации стиля (минимум 3 символа)"
        >
          <Input
            placeholder="профессиональный, заботливый, дружелюбный..."
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="System Prompt"
          name="system_prompt"
          rules={[
            { required: true, message: "Введите промпт" },
            { min: 20, message: "Минимум 20 символов" },
          ]}
          tooltip="Инструкции для модели LLM, определяющие тон и стиль ответа"
        >
          <TextArea
            rows={10}
            placeholder="Ты — профессиональный ассистент бренда Art-Visage. Используй деловой, профессиональный тон. Будь вежливым, но кратким. Всегда благодари за отзыв..."
            showCount
            maxLength={2000}
          />
        </Form.Item>

        <Form.Item
          label="Модель LLM"
          name="llm_model"
          rules={[{ required: true, message: "Выберите модель" }]}
          tooltip="Языковая модель для генерации ответов"
        >
          <Select
            placeholder="Выберите модель"
            size="large"
            optionLabelProp="label"
          >
            <Select.Option value="deepseek-chat" label="DeepSeek Chat">
              <div style={{ whiteSpace: 'normal' }}>
                <span style={{ fontWeight: 600, color: '#1677ff' }}>DeepSeek Chat</span>
                <br />
                <span style={{ fontSize: 12, color: '#999' }}>Быстрая и качественная модель</span>
              </div>
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Температура"
          name="temperature"
          rules={[{ required: true, message: "Укажите температуру" }]}
          tooltip="Креативность ответов: 0 = строго по факту, 1 = максимально креативно"
        >
          <InputNumber
            min={0}
            max={1}
            step={0.1}
            style={{ width: "100%" }}
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Активен"
          name="is_active"
          valuePropName="checked"
          tooltip="Показывать этот стиль в списке при создании правил маршрутизации"
        >
          <Switch checkedChildren="Да" unCheckedChildren="Нет" />
        </Form.Item>

        <Form.Item
          label="Использовать по умолчанию"
          name="is_default"
          valuePropName="checked"
          tooltip="Использовать этот стиль, если стиль не выбран явно"
        >
          <Switch checkedChildren="Да" unCheckedChildren="Нет" />
        </Form.Item>

        <Space>
          <Button type="primary" htmlType="submit" loading={formLoading}>
            Сохранить
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
